import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

// Runtime mode detection (not build-time): the same `dist/` bundle is served
// from pages.dev (cloud, Clerk auth) and from the venue LAN box (local, device
// pairing). On mount we probe GET /api/config → {mode:"local"|"cloud", env:"prod"|"staging"|"dev"}.
// Failure, timeout (500ms), or a *.pages.dev hostname defaults mode to "cloud" so
// the cloud deployment can never get stuck behind a missing endpoint.
//
// `env` (which of the three environments) is a SEPARATE signal from `mode`
// (local vs cloud) — both staging and dev exist on BOTH sides: staging as the
// cloud worker (pos-staging-api) + the staging LAN box, dev as the two halves
// running on a laptop (docs/DEV-ENV.md). The pages.dev short-circuit below
// only resolves `mode` instantly; the /api/config fetch always fires
// regardless, so `env` still resolves on pages.dev-hosted staging (e.g.
// pos-staging-9ph.pages.dev).

export type RuntimeMode = "local" | "cloud" | "loading";
export type PosEnv = "prod" | "staging" | "dev" | "unknown";

export const DEVICE_TOKEN_KEY = "pos.device.token";
export const DEVICE_ID_KEY = "pos.device.id";
// Local-admin console session (device runtime): { token, staff:{id,name,role} }.
// sessionStorage (not local) — a closed tab ends the admin session, like a POS.
export const LOCAL_ADMIN_KEY = "pos.localadmin.session";

// Module-level cache: one probe per page load, shared by every consumer
// (StrictMode double-mount included). pages.dev is always the cloud
// deployment — resolved at module load so cloud never even flashes "loading".
// `env`, however, is NOT resolvable from the hostname alone (staging can be
// pages.dev-hosted too), so it stays null until the fetch below resolves it.
let cachedMode: "local" | "cloud" | null =
  typeof window !== "undefined" && window.location.hostname.endsWith(".pages.dev") ? "cloud" : null;
let cachedEnv: PosEnv | null = null;
let inflight: Promise<{ mode: "local" | "cloud"; env: PosEnv }> | null = null;

function probeConfig(): Promise<{ mode: "local" | "cloud"; env: PosEnv }> {
  if (cachedMode && cachedEnv) return Promise.resolve({ mode: cachedMode, env: cachedEnv });
  if (inflight) return inflight;
  inflight = (async () => {
    // Two attempts, 3s each: a slow first response (dev proxy warm-up, box
    // just rebooted) must not permanently miscache "cloud" on a local box —
    // that would show Clerk instead of the pairing screen. A real cloud
    // deployment answers fast either way (404 on old workers → "cloud").
    for (let attempt = 0; attempt < 2; attempt++) {
      try {
        const ctrl = new AbortController();
        const t = setTimeout(() => ctrl.abort(), 3000);
        const res = await fetch("/api/config", { signal: ctrl.signal });
        clearTimeout(t);
        if (res.ok) {
          const j = (await res.json()) as { mode?: string; env?: string };
          cachedMode = cachedMode ?? (j.mode === "local" ? "local" : "cloud");
          // Allowlist, not a passthrough: an unrecognised value means an older
          // or misconfigured worker, and "prod" (no banner) is the safe read.
          cachedEnv = j.env === "staging" ? "staging" : j.env === "dev" ? "dev" : "prod";
        } else {
          // endpoint answered: this is a pre-/api/config cloud worker (no env field)
          cachedMode = cachedMode ?? "cloud";
          cachedEnv = "unknown";
        }
        return { mode: cachedMode, env: cachedEnv };
      } catch {
        /* timeout or network — retry once before assuming cloud/unknown */
      }
    }
    cachedMode = cachedMode ?? "cloud";
    cachedEnv = cachedEnv ?? "unknown";
    return { mode: cachedMode, env: cachedEnv };
  })();
  return inflight;
}

const RuntimeModeContext = createContext<RuntimeMode>("loading");
const PosEnvContext = createContext<PosEnv>("unknown");

export function RuntimeModeProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<RuntimeMode>(cachedMode ?? "loading");
  const [env, setEnv] = useState<PosEnv>(cachedEnv ?? "unknown");
  useEffect(() => {
    let alive = true;
    probeConfig().then((r) => {
      if (!alive) return;
      setMode(r.mode);
      setEnv(r.env);
    });
    return () => { alive = false; };
  }, []);
  // Children render immediately: public surfaces (kiosk, /menu, /t/:id, /qr,
  // /cfd) don't depend on the mode, and RequireAuth handles "loading" itself.
  return (
    <RuntimeModeContext.Provider value={mode}>
      <PosEnvContext.Provider value={env}>{children}</PosEnvContext.Provider>
    </RuntimeModeContext.Provider>
  );
}

export function useRuntimeMode(): RuntimeMode {
  return useContext(RuntimeModeContext);
}

// "staging"/"dev" only once /api/config confirms it; "unknown" (never a
// test-environment value) while loading or on probe failure — the warning must
// never flash on production, so it fails toward silence, not toward alarm.
export function usePosEnv(): PosEnv {
  return useContext(PosEnvContext);
}
